/**
 * Position-management overlays (mandate §2) — composed surface, DEC-065 precedent.
 *
 * The package drew the row buttons and left them inert; it never designed the dialogs behind them. These are the
 * minimum coherent surfaces in the app's own grammar (the order-confirm modal's): a close confirmation that names
 * the position and its floating P&L, a close-all confirmation that names the count, and a modify editor whose
 * emptied field REMOVES a level — stated on the field, not implied. Every confirm goes through the exec slice,
 * which busy-guards the double click and reads the table back from the server's own positions array.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:360px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const heading = css('font-size:13px;font-weight:600;margin:0 0 6px');
const body = css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 14px');
const row = css('display:flex;gap:8px;justify-content:flex-end');
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const dangerBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--neg) 45%, transparent);background:color-mix(in srgb, var(--neg) 10%, transparent);color:var(--neg);cursor:pointer',
);
const primaryBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--pos) 45%, transparent);background:color-mix(in srgb, var(--pos) 10%, transparent);color:var(--pos);cursor:pointer',
);
const field = css(
  'width:100%;padding:8px 12px;border-radius:10px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:12px;font-variant-numeric:tabular-nums',
);
const label = css(
  'display:block;font-size:9.5px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted);margin:0 0 5px',
);

export function PositionActions({ m }: { m: Partial<ViewModel> }) {
  const busy = !!m.posBusy;

  if (m.posCloseConfirm) {
    const p = m.posCloseConfirm;
    return (
      <div style={overlay} role="dialog" aria-modal="true" aria-label={`Close ${p.sym} position`}>
        <div style={sheet}>
          <h2 style={heading}>Close {p.sym}?</h2>
          <p style={body}>
            {p.vol} lots, floating {p.pnl}. The position closes at the market — the table below will show what
            the server recorded, not an estimate.
          </p>
          <div style={row}>
            <button style={ghostBtn} onClick={m.posCloseCancel} disabled={busy}>
              Keep it open
            </button>
            <button
              style={dangerBtn}
              onClick={m.posCloseGo}
              disabled={busy}
              aria-label={`Confirm close ${p.sym}`}
            >
              {busy ? 'Closing…' : `Close ${p.vol} ${p.sym}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (m.posCloseAllConfirmOpen) {
    const n = Number(m.posCloseAllCount ?? 0);
    return (
      <div style={overlay} role="dialog" aria-modal="true" aria-label="Close all positions">
        <div style={sheet}>
          <h2 style={heading}>
            Close all {n} position{n === 1 ? '' : 's'}?
          </h2>
          <p style={body}>
            Every open position on this account closes at the market. If any cannot be closed, you will be
            told exactly which remained open — nothing is averaged away.
          </p>
          <div style={row}>
            <button style={ghostBtn} onClick={m.posCloseAllCancel} disabled={busy}>
              Cancel
            </button>
            <button
              style={dangerBtn}
              onClick={m.posCloseAllGo}
              disabled={busy}
              aria-label="Confirm close all"
            >
              {busy ? 'Closing…' : 'Close all'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (m.posModifyDraft) {
    const d = m.posModifyDraft;
    return (
      <div style={overlay} role="dialog" aria-modal="true" aria-label={`Modify ${d.sym} levels`}>
        <div style={sheet}>
          <h2 style={heading}>Modify {d.sym}</h2>
          <p style={body}>
            Set the protective levels. An emptied field removes that level from the position.
          </p>
          <div style={css('display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px')}>
            <div>
              <label style={label} htmlFor="pf-modify-sl">
                Stop loss
              </label>
              <input
                id="pf-modify-sl"
                style={field}
                value={d.sl}
                inputMode="decimal"
                placeholder="none"
                onChange={(e) => m.posModifySetSl?.(e.target.value)}
              />
            </div>
            <div>
              <label style={label} htmlFor="pf-modify-tp">
                Take profit
              </label>
              <input
                id="pf-modify-tp"
                style={field}
                value={d.tp}
                inputMode="decimal"
                placeholder="none"
                onChange={(e) => m.posModifySetTp?.(e.target.value)}
              />
            </div>
          </div>
          <div style={row}>
            <button style={ghostBtn} onClick={m.posModifyCancel} disabled={busy}>
              Cancel
            </button>
            <button
              style={primaryBtn}
              onClick={m.posModifyGo}
              disabled={busy}
              aria-label={`Save ${d.sym} levels`}
            >
              {busy ? 'Saving…' : 'Save levels'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
